import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, Phone, Mail, Building2, GraduationCap, Scale, Plus, Search,
  Filter, MoreVertical, CheckCircle, XCircle, Clock, PhoneCall,
  Send, Trash2, Edit2, BarChart3, TrendingUp, Calendar, ArrowRight,
  X, ChevronDown, RefreshCw, FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tipos de negocio con iconos
const TIPOS_NEGOCIO = {
  colegio: { label: 'Colegio', icon: GraduationCap, color: 'bg-green-500' },
  bufete: { label: 'Bufete', icon: Scale, color: 'bg-purple-500' },
  empresa: { label: 'Empresa', icon: Building2, color: 'bg-blue-500' },
  independiente: { label: 'Independiente', icon: Users, color: 'bg-orange-500' },
  otro: { label: 'Otro', icon: Building2, color: 'bg-gray-500' }
};

// Productos con colores
const PRODUCTOS = {
  agente_virtual: { label: 'Agente Virtual', color: 'bg-purple-600', textColor: 'text-purple-600' },
  tutora: { label: 'Tutora IA', color: 'bg-green-600', textColor: 'text-green-600' },
  lila: { label: 'LILA Legal', color: 'bg-indigo-600', textColor: 'text-indigo-600' }
};

// Estados con colores
const ESTADOS = {
  pendiente: { label: 'Pendiente', color: 'bg-gray-400', textColor: 'text-gray-600' },
  llamando: { label: 'Llamando', color: 'bg-yellow-500', textColor: 'text-yellow-600' },
  contactado: { label: 'Contactado', color: 'bg-blue-500', textColor: 'text-blue-600' },
  interesado: { label: 'Interesado', color: 'bg-green-500', textColor: 'text-green-600' },
  no_interesado: { label: 'No Interesado', color: 'bg-red-400', textColor: 'text-red-600' },
  seguimiento: { label: 'Seguimiento', color: 'bg-orange-500', textColor: 'text-orange-600' },
  cliente: { label: 'Cliente', color: 'bg-emerald-500', textColor: 'text-emerald-600' },
  no_contesta: { label: 'No Contesta', color: 'bg-gray-500', textColor: 'text-gray-600' }
};

// Resultados de llamada
const RESULTADOS_LLAMADA = {
  no_contesta: 'No Contesta',
  buzon: 'Buzón de Voz',
  ocupado: 'Ocupado',
  interesado: 'Interesado',
  no_interesado: 'No Interesado',
  pide_info: 'Pide Información',
  agendar_demo: 'Agendar Demo',
  transferir_humano: 'Transferir a Humano'
};

// Componente principal del CRM
export default function CRMVentas({ token, onClose }) {
  const [estadisticas, setEstadisticas] = useState(null);
  const [prospectos, setProspectos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({ estado: '', producto: '', buscar: '' });
  const [showNuevoProspecto, setShowNuevoProspecto] = useState(false);
  const [showRegistrarLlamada, setShowRegistrarLlamada] = useState(null);
  const [prospectoSeleccionado, setProspectoSeleccionado] = useState(null);
  const [vista, setVista] = useState('lista'); // lista, estadisticas

  // Cargar estadísticas
  const cargarEstadisticas = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/crm/estadisticas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEstadisticas(data);
      }
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    }
  }, [token]);

  // Cargar prospectos
  const cargarProspectos = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtros.estado) params.append('estado', filtros.estado);
      if (filtros.producto) params.append('producto', filtros.producto);
      if (filtros.buscar) params.append('buscar', filtros.buscar);

      const res = await fetch(`${API_URL}/api/crm/prospectos?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setProspectos(data.prospectos || []);
      }
    } catch (error) {
      console.error('Error cargando prospectos:', error);
    } finally {
      setLoading(false);
    }
  }, [token, filtros]);

  useEffect(() => {
    cargarEstadisticas();
    cargarProspectos();
  }, [cargarEstadisticas, cargarProspectos]);

  // Enviar email a prospecto
  const enviarEmail = async (prospectoId) => {
    try {
      const res = await fetch(`${API_URL}/api/crm/enviar-email/${prospectoId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        alert('Email enviado exitosamente');
        cargarProspectos();
      } else {
        const error = await res.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      alert('Error enviando email');
    }
  };

  // Eliminar prospecto
  const eliminarProspecto = async (prospectoId) => {
    if (!window.confirm('¿Estás seguro de eliminar este prospecto?')) return;
    try {
      const res = await fetch(`${API_URL}/api/crm/prospectos/${prospectoId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        cargarProspectos();
        cargarEstadisticas();
      }
    } catch (error) {
      alert('Error eliminando prospecto');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <Users size={28} />
              </div>
              <div>
                <h2 className="text-2xl font-bold">CRM de Ventas</h2>
                <p className="text-purple-200">Gestión de prospectos y llamadas</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setVista(vista === 'lista' ? 'estadisticas' : 'lista')}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title={vista === 'lista' ? 'Ver estadísticas' : 'Ver lista'}
              >
                {vista === 'lista' ? <BarChart3 size={24} /> : <Users size={24} />}
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Estadísticas rápidas */}
          {estadisticas && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <div className="bg-white/10 rounded-xl p-4">
                <div className="text-3xl font-bold">{estadisticas.total_prospectos}</div>
                <div className="text-purple-200 text-sm">Total Prospectos</div>
              </div>
              <div className="bg-white/10 rounded-xl p-4">
                <div className="text-3xl font-bold text-green-300">
                  {estadisticas.prospectos_por_estado?.interesado || 0}
                </div>
                <div className="text-purple-200 text-sm">Interesados</div>
              </div>
              <div className="bg-white/10 rounded-xl p-4">
                <div className="text-3xl font-bold">{estadisticas.llamadas_hoy}</div>
                <div className="text-purple-200 text-sm">Llamadas Hoy</div>
              </div>
              <div className="bg-white/10 rounded-xl p-4">
                <div className="text-3xl font-bold text-yellow-300">
                  {estadisticas.tasa_conversion}%
                </div>
                <div className="text-purple-200 text-sm">Conversión</div>
              </div>
            </div>
          )}
        </div>

        {/* Contenido */}
        <div className="flex-1 overflow-auto p-6">
          {vista === 'estadisticas' ? (
            <EstadisticasView estadisticas={estadisticas} />
          ) : (
            <>
              {/* Barra de acciones */}
              <div className="flex flex-wrap items-center gap-4 mb-6">
                <button
                  onClick={() => setShowNuevoProspecto(true)}
                  className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Plus size={20} />
                  Nuevo Prospecto
                </button>

                <div className="flex-1 flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-2">
                  <Search size={20} className="text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar por nombre, empresa o teléfono..."
                    value={filtros.buscar}
                    onChange={(e) => setFiltros({ ...filtros, buscar: e.target.value })}
                    className="flex-1 bg-transparent outline-none"
                  />
                </div>

                <select
                  value={filtros.estado}
                  onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}
                  className="bg-gray-100 rounded-lg px-3 py-2 outline-none"
                >
                  <option value="">Todos los estados</option>
                  {Object.entries(ESTADOS).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>

                <select
                  value={filtros.producto}
                  onChange={(e) => setFiltros({ ...filtros, producto: e.target.value })}
                  className="bg-gray-100 rounded-lg px-3 py-2 outline-none"
                >
                  <option value="">Todos los productos</option>
                  {Object.entries(PRODUCTOS).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>

                <button
                  onClick={() => { cargarProspectos(); cargarEstadisticas(); }}
                  className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  title="Actualizar"
                >
                  <RefreshCw size={20} className="text-gray-600" />
                </button>
              </div>

              {/* Lista de prospectos */}
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
                </div>
              ) : prospectos.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No hay prospectos aún</p>
                  <button
                    onClick={() => setShowNuevoProspecto(true)}
                    className="mt-4 text-purple-600 hover:underline"
                  >
                    Agregar el primero
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {prospectos.map((prospecto) => (
                    <ProspectoCard
                      key={prospecto.id}
                      prospecto={prospecto}
                      onLlamar={() => setShowRegistrarLlamada(prospecto)}
                      onEmail={() => enviarEmail(prospecto.id)}
                      onEliminar={() => eliminarProspecto(prospecto.id)}
                      onVer={() => setProspectoSeleccionado(prospecto)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Modal Nuevo Prospecto */}
        {showNuevoProspecto && (
          <NuevoProspectoModal
            token={token}
            onClose={() => setShowNuevoProspecto(false)}
            onSuccess={() => {
              setShowNuevoProspecto(false);
              cargarProspectos();
              cargarEstadisticas();
            }}
          />
        )}

        {/* Modal Registrar Llamada */}
        {showRegistrarLlamada && (
          <RegistrarLlamadaModal
            token={token}
            prospecto={showRegistrarLlamada}
            onClose={() => setShowRegistrarLlamada(null)}
            onSuccess={() => {
              setShowRegistrarLlamada(null);
              cargarProspectos();
              cargarEstadisticas();
            }}
          />
        )}

        {/* Modal Ver Prospecto */}
        {prospectoSeleccionado && (
          <VerProspectoModal
            token={token}
            prospecto={prospectoSeleccionado}
            onClose={() => setProspectoSeleccionado(null)}
          />
        )}
      </div>
    </div>
  );
}

// Tarjeta de prospecto
function ProspectoCard({ prospecto, onLlamar, onEmail, onEliminar, onVer }) {
  const tipo = TIPOS_NEGOCIO[prospecto.tipo_negocio] || TIPOS_NEGOCIO.otro;
  const producto = PRODUCTOS[prospecto.producto_objetivo] || PRODUCTOS.agente_virtual;
  const estado = ESTADOS[prospecto.estado] || ESTADOS.pendiente;
  const TipoIcon = tipo.icon;

  return (
    <div 
      className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow cursor-pointer"
      onClick={onVer}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className={`p-3 ${tipo.color} text-white rounded-xl`}>
            <TipoIcon size={24} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{prospecto.empresa}</h3>
            {prospecto.contacto && (
              <p className="text-sm text-gray-600">
                {prospecto.contacto} {prospecto.cargo && `- ${prospecto.cargo}`}
              </p>
            )}
            <div className="flex items-center gap-3 mt-2 text-sm">
              <span className="flex items-center gap-1 text-gray-500">
                <Phone size={14} />
                {prospecto.telefono}
              </span>
              {prospecto.email && (
                <span className="flex items-center gap-1 text-gray-500">
                  <Mail size={14} />
                  {prospecto.email}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 text-xs font-medium rounded-full ${producto.color} text-white`}>
            {producto.label}
          </span>
          <span className={`px-3 py-1 text-xs font-medium rounded-full ${estado.color} text-white`}>
            {estado.label}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <span className="flex items-center gap-1">
            <PhoneCall size={14} />
            {prospecto.total_llamadas} llamadas
          </span>
          {prospecto.ultima_llamada && (
            <span className="flex items-center gap-1">
              <Calendar size={14} />
              Última: {new Date(prospecto.ultima_llamada).toLocaleDateString()}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={onLlamar}
            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
            title="Registrar llamada"
          >
            <Phone size={18} />
          </button>
          {prospecto.email && (
            <button
              onClick={onEmail}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Enviar email"
            >
              <Send size={18} />
            </button>
          )}
          <button
            onClick={onEliminar}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Eliminar"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal Nuevo Prospecto
function NuevoProspectoModal({ token, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    empresa: '',
    contacto: '',
    cargo: '',
    telefono: '',
    email: '',
    tipo_negocio: 'empresa',
    producto_objetivo: 'agente_virtual',
    notas: '',
    ciudad: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/crm/prospectos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        onSuccess();
      } else {
        const error = await res.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      alert('Error creando prospecto');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900">Nuevo Prospecto</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Empresa / Institución *
            </label>
            <input
              type="text"
              required
              value={formData.empresa}
              onChange={(e) => setFormData({ ...formData, empresa: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Nombre de la empresa o colegio"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contacto
              </label>
              <input
                type="text"
                value={formData.contacto}
                onChange={(e) => setFormData({ ...formData, contacto: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Nombre del contacto"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cargo
              </label>
              <input
                type="text"
                value={formData.cargo}
                onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Director, Gerente, etc."
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Teléfono *
              </label>
              <input
                type="tel"
                required
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="+507 6xxx-xxxx"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="email@ejemplo.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de Negocio
              </label>
              <select
                value={formData.tipo_negocio}
                onChange={(e) => setFormData({ ...formData, tipo_negocio: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {Object.entries(TIPOS_NEGOCIO).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Producto a Ofrecer
              </label>
              <select
                value={formData.producto_objetivo}
                onChange={(e) => setFormData({ ...formData, producto_objetivo: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {Object.entries(PRODUCTOS).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ciudad
            </label>
            <input
              type="text"
              value={formData.ciudad}
              onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Ciudad de Panamá, David, etc."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notas
            </label>
            <textarea
              value={formData.notas}
              onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Información adicional sobre el prospecto..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Guardando...' : 'Guardar Prospecto'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Modal Registrar Llamada
function RegistrarLlamadaModal({ token, prospecto, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    resultado: 'contactado',
    duracion_segundos: '',
    notas: '',
    enviar_email: false
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/crm/llamadas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          prospecto_id: prospecto.id,
          resultado: formData.resultado,
          duracion_segundos: formData.duracion_segundos ? parseInt(formData.duracion_segundos) : null,
          notas: formData.notas,
          enviar_email: formData.enviar_email
        })
      });

      if (res.ok) {
        onSuccess();
      } else {
        const error = await res.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      alert('Error registrando llamada');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="border-b p-4 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Registrar Llamada</h3>
            <p className="text-sm text-gray-500">{prospecto.empresa}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Resultado de la Llamada
            </label>
            <select
              value={formData.resultado}
              onChange={(e) => setFormData({ ...formData, resultado: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              {Object.entries(RESULTADOS_LLAMADA).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Duración (segundos)
            </label>
            <input
              type="number"
              value={formData.duracion_segundos}
              onChange={(e) => setFormData({ ...formData, duracion_segundos: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Ej: 120"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notas
            </label>
            <textarea
              value={formData.notas}
              onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Detalles de la conversación..."
            />
          </div>

          {prospecto.email && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.enviar_email}
                onChange={(e) => setFormData({ ...formData, enviar_email: e.target.checked })}
                className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700">
                Enviar email de seguimiento automáticamente
              </span>
            </label>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Guardando...' : 'Registrar Llamada'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Modal Ver Prospecto
function VerProspectoModal({ token, prospecto, onClose }) {
  const [llamadas, setLlamadas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cargarLlamadas = async () => {
      try {
        const res = await fetch(`${API_URL}/api/crm/llamadas/${prospecto.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setLlamadas(data.llamadas || []);
        }
      } catch (error) {
        console.error('Error cargando llamadas:', error);
      } finally {
        setLoading(false);
      }
    };
    cargarLlamadas();
  }, [token, prospecto.id]);

  const tipo = TIPOS_NEGOCIO[prospecto.tipo_negocio] || TIPOS_NEGOCIO.otro;
  const producto = PRODUCTOS[prospecto.producto_objetivo] || PRODUCTOS.agente_virtual;
  const estado = ESTADOS[prospecto.estado] || ESTADOS.pendiente;

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900">Detalle del Prospecto</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Info del prospecto */}
          <div className="flex items-start gap-4">
            <div className={`p-4 ${tipo.color} text-white rounded-xl`}>
              {React.createElement(tipo.icon, { size: 32 })}
            </div>
            <div className="flex-1">
              <h4 className="text-2xl font-bold text-gray-900">{prospecto.empresa}</h4>
              {prospecto.contacto && (
                <p className="text-gray-600">{prospecto.contacto} {prospecto.cargo && `- ${prospecto.cargo}`}</p>
              )}
              <div className="flex flex-wrap gap-2 mt-2">
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${producto.color} text-white`}>
                  {producto.label}
                </span>
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${estado.color} text-white`}>
                  {estado.label}
                </span>
              </div>
            </div>
          </div>

          {/* Datos de contacto */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-xl">
            <div>
              <p className="text-sm text-gray-500">Teléfono</p>
              <p className="font-medium">{prospecto.telefono}</p>
            </div>
            {prospecto.email && (
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium">{prospecto.email}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-500">País</p>
              <p className="font-medium">{prospecto.pais}</p>
            </div>
            {prospecto.ciudad && (
              <div>
                <p className="text-sm text-gray-500">Ciudad</p>
                <p className="font-medium">{prospecto.ciudad}</p>
              </div>
            )}
          </div>

          {prospecto.notas && (
            <div className="p-4 bg-yellow-50 rounded-xl">
              <p className="text-sm font-medium text-yellow-800 mb-1">Notas</p>
              <p className="text-yellow-900">{prospecto.notas}</p>
            </div>
          )}

          {/* Historial de llamadas */}
          <div>
            <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <PhoneCall size={18} />
              Historial de Llamadas ({llamadas.length})
            </h5>
            {loading ? (
              <p className="text-gray-500">Cargando...</p>
            ) : llamadas.length === 0 ? (
              <p className="text-gray-500">No hay llamadas registradas</p>
            ) : (
              <div className="space-y-3">
                {llamadas.map((llamada) => (
                  <div key={llamada.id} className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className={`px-2 py-1 text-xs rounded ${
                        llamada.resultado === 'interesado' ? 'bg-green-100 text-green-700' :
                        llamada.resultado === 'no_interesado' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {RESULTADOS_LLAMADA[llamada.resultado] || llamada.resultado}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(llamada.created_at).toLocaleString()}
                      </span>
                    </div>
                    {llamada.notas && (
                      <p className="text-sm text-gray-600 mt-2">{llamada.notas}</p>
                    )}
                    {llamada.email_enviado && (
                      <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                        <Mail size={12} /> Email enviado
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Vista de Estadísticas
function EstadisticasView({ estadisticas }) {
  if (!estadisticas) return null;

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-900">Estadísticas del CRM</h3>
      
      {/* Por Estado */}
      <div className="bg-white border rounded-xl p-6">
        <h4 className="font-semibold text-gray-700 mb-4">Prospectos por Estado</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(ESTADOS).map(([key, { label, color }]) => (
            <div key={key} className="text-center">
              <div className={`w-12 h-12 ${color} rounded-full flex items-center justify-center mx-auto text-white font-bold text-lg`}>
                {estadisticas.prospectos_por_estado?.[key] || 0}
              </div>
              <p className="text-sm text-gray-600 mt-2">{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Por Producto */}
      <div className="bg-white border rounded-xl p-6">
        <h4 className="font-semibold text-gray-700 mb-4">Prospectos por Producto</h4>
        <div className="grid grid-cols-3 gap-4">
          {Object.entries(PRODUCTOS).map(([key, { label, color }]) => (
            <div key={key} className={`p-4 ${color} rounded-xl text-white text-center`}>
              <div className="text-3xl font-bold">
                {estadisticas.prospectos_por_producto?.[key] || 0}
              </div>
              <p className="text-sm opacity-90">{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Métricas adicionales */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border rounded-xl p-4 text-center">
          <PhoneCall className="mx-auto text-green-500 mb-2" size={32} />
          <div className="text-2xl font-bold">{estadisticas.llamadas_semana}</div>
          <p className="text-sm text-gray-500">Llamadas esta semana</p>
        </div>
        <div className="bg-white border rounded-xl p-4 text-center">
          <Mail className="mx-auto text-blue-500 mb-2" size={32} />
          <div className="text-2xl font-bold">{estadisticas.emails_enviados}</div>
          <p className="text-sm text-gray-500">Emails enviados</p>
        </div>
        <div className="bg-white border rounded-xl p-4 text-center">
          <TrendingUp className="mx-auto text-purple-500 mb-2" size={32} />
          <div className="text-2xl font-bold">{estadisticas.tasa_conversion}%</div>
          <p className="text-sm text-gray-500">Tasa de conversión</p>
        </div>
        <div className="bg-white border rounded-xl p-4 text-center">
          <Users className="mx-auto text-orange-500 mb-2" size={32} />
          <div className="text-2xl font-bold">{estadisticas.total_prospectos}</div>
          <p className="text-sm text-gray-500">Total prospectos</p>
        </div>
      </div>
    </div>
  );
}
